import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { Box, Flex, Text, Stack, SimpleGrid, Spinner, Link, Badge } from "@chakra-ui/react";
import { Building2, Inbox, Flag, ClipboardList, BarChart3 } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { cachePut, cacheGet, safeQuery, safeAll } from "@/lib/offline";
import { Greeting, HomeCard, QuickLink, StatTile, EmptyState, RecentSubs } from "@/pages/home/shared";
import { colors } from "@/theme/colors";

const ACCENT_AMBER = { bg: "#FBEEDA", fg: "#8A5106" };
const ACCENT_POPPY = { bg: colors.poppyTint, fg: colors.poppy };
const ACCENT_TEAL = { bg: colors.tealTint, fg: colors.teal };
const ACCENT_SAGE = { bg: "#EAF0E7", fg: colors.sageDeep };

export default function SuperHome() {
  const { user, profile, companies } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [unknown, setUnknown] = useState(false);

  const load = useCallback(async () => {
    const { data, error } = await safeQuery(supabase.from("submissions")
      .select("id,company_id,template_title,template_type,worker_name,location_name,status,flagged,revision,created_at")
      .order("created_at", { ascending: false }).limit(500));
    if (error) {
      // Load failed: cached data (even a real zero) is real data; unknown only when nothing was ever loaded.
      const cached = cacheGet(user.id, "home_subs");
      setSubs(cached ? cached.subs || [] : []);
      setUnknown(!cached);
    } else {
      setSubs(data || []);
      setUnknown(false);
      cachePut(user.id, "home_subs", { subs: data || [] });
    }
    setLoading(false);
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Flex minH="40vh" align="center" justify="center"><Spinner color={colors.teal} borderWidth="3px" /></Flex>;

  const companyList = Object.values(companies || {});
  const active = subs.filter((s) => s.status !== "superseded");
  const pending = active.filter((s) => s.status === "pending");
  const flagged = pending.filter((s) => s.flagged);
  const recent = active.slice(0, 6);
  const companyName = (s) => companies?.[s.company_id]?.name || "—";

  return (
    <Box data-testid="home-super" maxW="1000px">
      <Greeting name={profile?.full_name} sub="Platform overview — all client companies." />

      <SimpleGrid columns={{ base: 2, lg: 4 }} gap="4" mb="6">
        <StatTile icon={Building2} label="Client companies" value={companyList.length} sub="tap to manage"
          accent={ACCENT_TEAL} to="/platform" testId="home-stat-companies" />
        <StatTile icon={Inbox} label="Total submissions" value={active.length} sub="across all companies"
          accent={ACCENT_SAGE} unavailable={unknown} to="/submissions" testId="home-stat-total" />
        <StatTile icon={ClipboardList} label="Pending review" value={pending.length} sub="platform-wide"
          accent={ACCENT_AMBER} unavailable={unknown} to="/submissions?status=pending" testId="home-stat-pending" />
        <StatTile icon={Flag} label="Flagged" value={flagged.length} sub="Fail / Critical answers"
          accent={ACCENT_POPPY} unavailable={unknown} to="/submissions?status=pending" testId="home-stat-flagged" />
      </SimpleGrid>

      <SimpleGrid columns={{ base: 1, lg: 3 }} gap="4">
        <Box gridColumn={{ base: "auto", lg: "span 2" }}>
          <HomeCard title="Recent activity (all companies)" testId="home-recent"
            action={<Link as={RouterLink} to="/submissions" fontSize="xs" color={colors.tealHover} fontWeight="600" data-testid="home-view-all-subs">View all</Link>}>
            {recent.length === 0 ? (
              <EmptyState>{unknown ? "You're offline — recent activity will appear once this device has loaded it." : "No submissions across the platform yet."}</EmptyState>
            ) : (
              <RecentSubs subs={recent} onOpen={(id) => navigate(`/submissions/${id}`)} companyOf={companyName} />
            )}
          </HomeCard>
        </Box>
        <Stack gap="3">
          <HomeCard title="By company" testId="home-by-company">
            <Stack gap="2.5">
              {companyList.map((c) => (
                <Flex key={c.id} justify="space-between" align="center">
                  <Text fontSize="sm" color={colors.textPrimary}>{c.name}</Text>
                  <Badge bg={colors.cream} color={colors.textPrimary} borderRadius="full" px="2.5">
                    {unknown ? "—" : active.filter((s) => s.company_id === c.id).length}
                  </Badge>
                </Flex>
              ))}
            </Stack>
          </HomeCard>
          <QuickLink icon={Building2} label="Companies & Users" desc="Onboard clients, assign users" to="/platform" testId="home-link-platform" />
          <QuickLink icon={ClipboardList} label="Master Templates" desc="The forms every client starts from" to="/templates" testId="home-link-templates" />
          <QuickLink icon={BarChart3} label="Analytics" desc="Cross-company trends" to="/analytics" testId="home-link-analytics" />
        </Stack>
      </SimpleGrid>
    </Box>
  );
}
